import React, { useEffect, useState } from 'react';
import { Gate } from './components/Gate';
import { WelcomeLetter } from './components/WelcomeLetter';
import { ProjectSearch } from './components/ProjectSearch';
import { BookGrid } from './components/BookGrid';
import { BonusStack } from './components/BonusStack';
/* Hidden for now: the paid unlock stack. Restore the import and the
   <UnlockStack /> line below, then renumber the sections after it. */
// import { UnlockStack } from './components/UnlockStack'
import { AgeLibraries } from './components/AgeLibraries';
import { ThreeSteps } from './components/ThreeSteps';
import { CommunityCard } from './components/CommunityCard';
import { accessLabel, mergePacks, unlockedFromUrl } from './data/tiers';
import type { PackId } from './data/tiers';
const STORE_KEY = 'workshop-member';
interface Member {
  email: string;
  unlocked: PackId[];
}
interface AppProps {
  startSignedIn?: boolean;
  startUnlocked?: PackId[];
}

/* localStorage rather than sessionStorage: this is a delivery page a parent
   comes back to for months, and making them retype their address every visit
   is exactly how people decide they have "lost" their access. */
function readStored(): Member | null {
  try {
    const saved = window.localStorage.getItem(STORE_KEY) ?? window.sessionStorage.getItem(STORE_KEY);
    if (!saved) return null;
    if (saved.startsWith('{')) {
      const parsed = JSON.parse(saved) as Partial<Member> & {
        tier?: string;
      };
      if (!parsed?.email) return null;
      return {
        email: parsed.email,
        unlocked: parsed.unlocked ?? []
      };
    }
    /* the very first version stored a bare email string */
    return {
      email: saved,
      unlocked: []
    };
  } catch {
    /* private windows block storage; the gate simply shows again */
    return null;
  }
}
function store(member: Member) {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(member));
  } catch {
    /* ignore */
  }
}
export function App({
  startSignedIn = false,
  startUnlocked = []
}: AppProps) {
  const [member, setMember] = useState<Member | null>(startSignedIn ? {
    email: 'member@example.com',
    unlocked: startUnlocked
  } : null);

  /* Whatever the delivery link carries, held separately so it still applies to
     somebody who has not typed their email yet. */
  const [fromLink, setFromLink] = useState<PackId[]>([]);
  useEffect(() => {
    if (startSignedIn) return;
    const linkPacks = typeof window === 'undefined' ? [] : unlockedFromUrl(window.location.search);
    setFromLink(linkPacks);
    const saved = readStored();
    if (!saved) return;
    const merged = {
      email: saved.email,
      unlocked: mergePacks(saved.unlocked, linkPacks)
    };
    setMember(merged);
    if (linkPacks.length) store(merged);
  }, [startSignedIn]);
  const signIn = (email: string) => {
    const next = {
      email,
      unlocked: fromLink
    };
    store(next);
    setMember(next);
  };
  const signOut = () => {
    try {
      window.localStorage.removeItem(STORE_KEY);
      window.sessionStorage.removeItem(STORE_KEY);
    } catch {
      /* ignore */
    }
    setMember(null);
  };
  if (member === null) {
    return <Gate onUnlock={signIn} data-id="element-0" data-mp="1d4zpahzp8im8" />;
  }
  return <div className="blueprint min-h-screen w-full bg-ground" data-id="element-1" data-mp="1d5srr6rmklyk">
      <header className="border-b-2 border-ink/10 bg-surface/70" data-id="element-2" data-mp="ygmu3n8fr3ss">
        <div className="mx-auto flex max-w-[76rem] flex-col gap-3 px-5 py-5 sm:flex-row sm:items-baseline sm:justify-between sm:px-8" data-id="element-3" data-mp="13eofietc3f7f">
          <div className="flex items-baseline gap-3" data-id="element-4" data-mp="1r5ams3vikvgd">
            <span className="font-display text-[19px] font-semibold text-ink" data-id="element-5" data-mp="1w3c2qfxn8enm">Little Engineer</span>
            <span aria-hidden="true" className="h-3 w-px bg-rule" data-id="element-6" data-mp="1i3kuywxpzui2" />
            <span className="smallcaps font-sans text-[10px] text-gold" data-id="element-7" data-mp="1i3b291laj98o">
              {accessLabel(member.unlocked)}
            </span>
          </div>
          <div className="flex items-baseline gap-4" data-id="element-8" data-mp="6nw59xt30hdk">
            <span className="font-sans text-[12px] text-muted" data-id="element-9" data-mp="6qidlznsr0wu">{member.email}</span>
            <button type="button" onClick={signOut} className="font-sans text-[12px] text-muted underline decoration-rule underline-offset-4 transition-colors duration-150 ease-out hover:text-ink hover:decoration-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink" data-id="element-10" data-mp="arkd8hlmlezc">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[76rem] px-5 pb-24 pt-14 sm:px-8 sm:pt-20" data-id="element-11" data-mp="c6ermz8ak5bl">
        <div className="flex flex-col gap-16 sm:gap-24" data-id="element-12" data-mp="19tx7sv4a89pm">
          <WelcomeLetter data-id="element-13" data-mp="14xydkqw9vxha" />
          <BookGrid data-id="element-14" data-mp="14xgbhdmxip1g" />
          <ProjectSearch data-id="element-15" data-mp="14v3sqy9ckanc" />

          <div className="flex items-center gap-5" aria-hidden="true" data-id="element-16" data-mp="zxaoiarltqpp">
            <span className="h-[2px] flex-1 bg-gold/45" data-id="element-17" data-mp="zzoh8cytaqu3" />
            <span className="font-display text-[11px] font-semibold uppercase tracking-[0.16em] text-gold" data-id="element-18" data-mp="vvgpe6x24urn">
              That was the collection. Now the extras
            </span>
            <span className="h-[2px] flex-1 bg-gold/45" data-id="element-19" data-mp="3gm7qlkk88phd" />
          </div>

          <BonusStack data-id="element-20" data-mp="1w4ezsdi9wtib" />
          {/* <UnlockStack unlocked={member.unlocked} /> */}
          <AgeLibraries data-id="element-21" data-mp="1w2lv33r80jcy" />
          <ThreeSteps data-id="element-22" data-mp="205ih74fyyxln" />
          <CommunityCard memberEmail={member.email} data-id="element-23" data-mp="2071xuw5jfany" />
        </div>
      </main>

      <footer className="border-t border-rule bg-surface/60" data-id="element-24" data-mp="254lf84xj83k4">
        <div className="mx-auto flex max-w-[76rem] flex-col gap-2 px-5 py-8 font-sans text-[13px] text-muted sm:flex-row sm:items-baseline sm:justify-between sm:px-8" data-id="element-25" data-mp="253a9hzkj0lqd">
          <p data-id="element-26" data-mp="2euemitqn5wik">Little Engineer Mega Kit · The Workshop</p>
          <p data-id="element-27" data-mp="2jtrchnct7jpd">Files are for your household. Please do not share the link.</p>
        </div>
      </footer>
    </div>;
}